import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Trash2, Inbox, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { Field } from "@/components/site/QuoteModal";
import { http, formatApiError } from "@/lib/api";
import { ReferralLinkMaker } from "@/components/site/ReferralLinkMaker";

const STATUSES = ["new", "contacted", "qualified", "closed"];
const SOURCE = { quote: "Quote", statement_review: "Statement review", contact: "Contact", booking: "Call booking", referral: "Referral $250" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="grid min-h-screen place-items-center bg-[#050B1F] p-4 grain relative">
      <form onSubmit={submit} className="relative w-full max-w-md rounded-[2rem] bg-white p-8" data-testid="admin-login-form">
        <Logo />
        <h1 className="mt-8 font-display text-3xl font-extrabold">Leads portal</h1>
        <p className="mt-1 text-sm text-emerald-950/70">Sign in to view incoming leads.</p>
        <div className="mt-6 space-y-4">
          <Field label="Email" testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label="Password" testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {err && <p className="mt-4 text-sm text-red-600" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} data-testid="admin-login-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0B2A6F] py-3.5 text-sm font-bold text-white hover:bg-[#050B1F] disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
        <Link to="/" className="mt-4 block text-center text-xs text-emerald-900/70 hover:text-emerald-900" data-testid="admin-back-home">Back to website</Link>
      </form>
    </div>
  );
};

const Leads = ({ user, onLogout }) => {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    http.get("/leads").then((r) => setLeads(Array.isArray(r.data) ? r.data : [])).catch(() => setLeads([]));
  }, []);
  const setStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await http.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
  };
  const counts = STATUSES.map((s) => [s, (leads ?? []).filter((l) => l.status === s).length]);

  return (
    <div className="min-h-screen bg-[#EAF3FF]" data-testid="admin-dashboard">
      <header className="flex items-center justify-between bg-[#050B1F] px-4 py-4 sm:px-8">
        <Link to="/" data-testid="admin-home-link"><Logo light /></Link>
        <div className="flex items-center gap-3 text-sm text-emerald-100">
          <span className="hidden sm:inline">{user.email}</span>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/20"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Leads</h1>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {counts.map(([s, n]) => (
            <div key={s} className="rounded-2xl bg-white p-5" data-testid={`admin-count-${s}`}>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-700">{s}</p>
              <p className="mt-1 font-display text-3xl font-extrabold">{n}</p>
            </div>
          ))}
        </div>
        <ReferralLinkMaker />
        <div className="mt-8 space-y-3">
          {leads === null && <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-700" />}
          {leads?.length === 0 && (
            <div className="rounded-3xl bg-white p-12 text-center text-emerald-900/70" data-testid="admin-empty"><Inbox className="mx-auto mb-3 h-8 w-8" />No leads yet.</div>
          )}
          {(leads ?? []).map((l) => (
            <div key={l.id} className="grid gap-4 rounded-3xl bg-white p-6 lg:grid-cols-12 lg:items-center" data-testid="admin-lead-row">
              <div className="lg:col-span-3">
                <p className="font-display text-lg font-bold">{l.name}</p>
                <p className="text-sm text-emerald-900/75">{l.business || "No business name"}</p>
                <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${l.source === "booking" ? "bg-[#0B2A6F] text-white" : "bg-lime-300"}`}>{SOURCE[l.source] ?? l.source}</span>
              </div>
              <div className="text-sm lg:col-span-3">
                <a href={`mailto:${l.email}`} className="block break-all font-medium text-emerald-700">{l.email}</a>
                <p className="text-emerald-900/75">{l.phone || "No phone"}</p>
              </div>
              <div className="text-sm text-emerald-950/80 lg:col-span-4">
                {l.slot_label && <p className="mb-1 inline-flex items-center gap-1.5 font-semibold text-[#0B2A6F]" data-testid="admin-booking-slot"><CalendarClock className="h-4 w-4" /> Call: {l.slot_label}</p>}
                {[l.industry, l.volume, l.equipment].filter(Boolean).join(" · ")}
                {l.referred_by && <p className="mb-1 font-semibold text-[#0B2A6F]" data-testid="admin-referred-by">Referred by: {l.referred_by}</p>}
                {l.message && <p className="mt-1 text-emerald-900/75">{l.message}</p>}
                <p className="mt-1 font-mono text-[11px] text-emerald-800/70">{new Date(l.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2 lg:col-span-2 lg:justify-end">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`admin-status-${l.id}`} className="rounded-full border border-emerald-900/15 bg-white px-3 py-2 text-sm font-medium capitalize">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(l.id)} data-testid={`admin-delete-${l.id}`} aria-label="Delete" className="rounded-full p-2 text-emerald-900/60 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => { await http.post("/auth/logout"); setUser(false); };
  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#050B1F]"><Loader2 className="h-6 w-6 animate-spin text-lime-400" /></div>;
  return user ? <Leads user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
